import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Send, Zap, CheckCircle, Paperclip, Mic, ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, ArrowRight, BarChart3, X, Calendar, Download, RefreshCw } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { ANALYTICS_DATA, ANALYTICS_DATA_BY_RANGE, CHANNEL_ANALYTICS, DATE_RANGES, ANALYTICS_CHAT_RESPONSES } from '../../data/analyticsData';
import V4AdHocChat from './V4AdHocChat';
import { PRIYA_ANALYTICS_CHANNEL_DD, PRIYA_ANALYTICS_KEYPRESS } from '../../data/priyaAnalyticsDeepDiveData';

const AGENT_NAMES = { dora: 'Dora', marsi: 'Marsi', buzz: 'Buzz' };

const CHANNEL_PILLS = [
  { id: null, label: 'All Channels' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'meta', label: 'Meta' },
  { id: 'blog', label: 'Blog / SEO' },
  { id: 'email', label: 'Email' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
];

/* ── Area Sparkline ── */
const AreaSparkline = ({ data, color, alert, width = 100, height = 36 }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;

  const points = data.map((v, i) => ({
    x: pad + (i / (data.length - 1)) * w,
    y: pad + h - ((v - min) / range) * h,
  }));

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x},${height} L${points[0].x},${height} Z`;
  const strokeColor = alert ? '#f87171' : color || '#818cf8';
  const fillColor = alert ? 'rgba(248,113,113,0.08)' : `${color || '#818cf8'}12`;
  const gradId = `grad-${strokeColor.replace('#', '')}-${width}`;

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={strokeColor} stopOpacity="0.2" />
          <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 3 : 1.5} fill={i === points.length - 1 ? strokeColor : 'transparent'} stroke={strokeColor} strokeWidth={i === points.length - 1 ? 0 : 0.5} />
      ))}
    </svg>
  );
};

/* ── Horizontal Funnel (fixed) ── */
const HorizontalFunnel = ({ stages, isDark }) => {
  const ts = isDark ? 'text-white/50' : 'text-slate-500';

  const colors = [
    { bg: isDark ? 'bg-indigo-500/15' : 'bg-indigo-50', border: isDark ? 'border-indigo-500/25' : 'border-indigo-200', text: isDark ? 'text-indigo-400' : 'text-indigo-600' },
    { bg: isDark ? 'bg-blue-500/15' : 'bg-blue-50', border: isDark ? 'border-blue-500/25' : 'border-blue-200', text: isDark ? 'text-blue-400' : 'text-blue-600' },
    { bg: isDark ? 'bg-emerald-500/15' : 'bg-emerald-50', border: isDark ? 'border-emerald-500/25' : 'border-emerald-200', text: isDark ? 'text-emerald-400' : 'text-emerald-600' },
    { bg: isDark ? 'bg-purple-500/15' : 'bg-purple-50', border: isDark ? 'border-purple-500/25' : 'border-purple-200', text: isDark ? 'text-purple-400' : 'text-purple-600' },
  ];

  return (
    <div className="flex items-center gap-0" data-testid="analytics-funnel">
      {stages.map((stage, i) => (
        <div key={i} className="flex items-center">
          {/* Conversion arrow BEFORE stage (between prev and current) */}
          {i > 0 && stage.conversion && stage.conversion !== '—' && (
            <div className="flex flex-col items-center mx-2">
              <ArrowRight className={`w-4 h-4 ${ts}`} />
              <span className={`text-[10px] font-bold ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{stage.conversion}</span>
            </div>
          )}
          {i > 0 && (!stage.conversion || stage.conversion === '—') && (
            <div className="flex items-center mx-2">
              <ArrowRight className={`w-4 h-4 ${ts}`} />
            </div>
          )}
          {/* Stage card */}
          <div
            className={`${colors[i % colors.length].bg} border ${colors[i % colors.length].border} rounded-xl px-5 py-3 flex flex-col items-center justify-center`}
            style={{ minWidth: 120 }}
            data-testid={`funnel-stage-${i}`}
          >
            <p className={`text-[10px] font-medium ${ts} mb-0.5 whitespace-nowrap`}>{stage.stage}</p>
            <p className={`text-xl font-bold ${colors[i % colors.length].text} leading-none`}>{stage.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── WoW Comparison (enlarged) ── */
const WoWComparison = ({ data, isDark, rangeLabel }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  const metrics = [
    { label: 'MQLs', this: data.thisWeek.mqls, last: data.lastWeek.mqls },
    { label: 'SQLs', this: data.thisWeek.sqls, last: data.lastWeek.sqls },
    { label: 'Pipeline', this: `$${(data.thisWeek.pipeline / 1000).toFixed(0)}K`, last: `$${(data.lastWeek.pipeline / 1000).toFixed(0)}K`, raw: data.thisWeek.pipeline, rawLast: data.lastWeek.pipeline },
    { label: 'Spend', this: `$${(data.thisWeek.spend / 1000).toFixed(1)}K`, last: `$${(data.lastWeek.spend / 1000).toFixed(1)}K`, raw: data.thisWeek.spend, rawLast: data.lastWeek.spend },
    { label: 'CPL', this: `$${data.thisWeek.cpl}`, last: `$${data.lastWeek.cpl}`, raw: data.thisWeek.cpl, rawLast: data.lastWeek.cpl, invert: true },
  ];

  return (
    <div className={`rounded-xl border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} px-4 py-3`} data-testid="wow-comparison">
      <p className={`text-[11px] font-semibold mb-3 ${tp}`}>Data Snapshot</p>
      <div className="grid grid-cols-5 gap-3">
        {metrics.map((m, i) => {
          const thisVal = m.raw !== undefined ? m.raw : m.this;
          const lastVal = m.rawLast !== undefined ? m.rawLast : m.last;
          const pctChange = typeof thisVal === 'number' && typeof lastVal === 'number' && lastVal !== 0
            ? ((thisVal - lastVal) / lastVal * 100).toFixed(0) : null;
          const isUp = pctChange > 0;
          const isGood = m.invert ? !isUp : isUp;

          return (
            <div key={i}>
              <p className={`text-[10px] ${ts} mb-1`}>{m.label}</p>
              <p className={`text-base font-bold ${tp} leading-tight`}>{m.this}</p>
              <div className="flex items-center gap-1 mt-1">
                <span className={`text-[9px] ${ts}`}>was {m.last}</span>
                {pctChange && (
                  <span className={`text-[9px] font-semibold ${isGood ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>
                    {isUp ? '+' : ''}{pctChange}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Trend Card ── */
const TrendCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/60') : border;
  const sparkColor = item.trendUp ? '#34d399' : '#f87171';

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3.5 py-3`} data-testid={`trend-${item.label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] ${ts}`}>{item.label}</p>
          <p className={`text-lg font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
          <div className="flex items-center gap-1 mt-0.5">
            {item.trend !== '—' && (item.trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
            <span className={`text-[10px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
          </div>
        </div>
        <AreaSparkline data={item.data} color={sparkColor} alert={item.alert} width={80} height={40} />
      </div>
    </div>
  );
};

/* ── Channel Row ── */
const ChannelRow = ({ item, isDark, isHighlighted, onDeepDive }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const rowBg = isHighlighted ? (isDark ? 'bg-indigo-500/8' : 'bg-indigo-500/5') : item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : '';

  return (
    <tr className={`${rowBg} border-b ${border}`} data-testid={`channel-row-${item.id}`}>
      <td className={`py-2.5 px-3 text-[11px] font-medium ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>
        {item.channel}
        {item.alert && <AlertTriangle className="w-3 h-3 inline ml-1" />}
      </td>
      <td className={`py-2.5 px-2 text-[11px] text-right ${ts}`}>{item.spend > 0 ? `$${item.spend.toLocaleString()}` : '\u2014'}</td>
      <td className={`py-2.5 px-2 text-[11px] text-right font-medium ${tp}`}>{item.leads}</td>
      <td className={`py-2.5 px-2 text-[11px] text-right ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.cpl}</td>
      <td className={`py-2.5 px-2 text-[11px] text-right font-medium ${parseFloat(item.roi) >= 3 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : parseFloat(item.roi) < 1 ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.roi}</td>
      <td className={`py-2.5 px-2 text-[11px] text-right ${ts}`}>{item.impressions}</td>
      <td className={`py-2.5 px-2 text-[11px] text-right ${item.trend.startsWith('+') ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</td>
      <td className={`py-2.5 px-2 text-right`}>
        {onDeepDive && (
          <button
            onClick={() => onDeepDive(item.id)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'}`}
            data-testid={`analytics-deepdive-${item.id}`}
          >
            <Zap className="w-3 h-3" /> Deep Dive
          </button>
        )}
      </td>
    </tr>
  );
};

/* ── Insight Card ── */
const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const typeStyles = {
    recommendation: { icon: Zap, color: isDark ? 'text-indigo-400' : 'text-indigo-600' },
    alert: { icon: AlertTriangle, color: isDark ? 'text-red-400' : 'text-red-500' },
    insight: { icon: TrendingUp, color: isDark ? 'text-emerald-400' : 'text-emerald-600' },
  };
  const st = typeStyles[item.type] || typeStyles.insight;
  const Icon = st.icon;
  const thumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`insight-${item.id}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${st.color}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <div className="flex items-center gap-1.5 mt-1">
          {thumb && <img src={thumb} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
          <span className={`text-[9px] ${ts}`}>{AGENT_NAMES[item.agent]} · {item.time}</span>
        </div>
      </div>
    </div>
  );
};

/* ── Chat Message ── */
const ChatMessage = ({ msg, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  if (msg.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}`}>{msg.text}</div>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <img src={AGENT_THUMBS.dora} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
      <div className={`text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
    </div>
  );
};

/* ════════════════════════════════════════ */
/* ── Main Component ── */
/* ════════════════════════════════════════ */

const V4MktAnalytics = ({ isDark, onWorkflowToggle, workflowPanelOpen, navContext, activeUser, analyticsAdHocScenario, onAnalyticsAdHocDone }) => {
  const [channelFilter, setChannelFilter] = useState(navContext?.channel || null);
  const [dateRange, setDateRange] = useState('weekly');
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [deepDiveChannelId, setDeepDiveChannelId] = useState(null);
  const messagesEndRef = useRef(null);

  const isPriya = activeUser?.id === 'marketing';

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => {
    if (navContext?.channel) setChannelFilter(navContext.channel);
  }, [navContext]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  /* ── Derived data based on channel + date range ── */
  const rangeData = ANALYTICS_DATA_BY_RANGE[dateRange] || ANALYTICS_DATA_BY_RANGE.weekly;

  const activeFunnel = useMemo(() => {
    if (!channelFilter) return rangeData.funnel;
    const cf = CHANNEL_ANALYTICS.funnel[channelFilter];
    if (!cf) return rangeData.funnel;
    return [
      { stage: 'Visitors', value: cf.visitors },
      { stage: 'MQLs', value: String(cf.mqls), conversion: cf.visitors !== '—' ? ((cf.mqls / parseFloat(cf.visitors.replace('K', '')) / 10).toFixed(2) + '%') : '—' },
      { stage: 'SQLs', value: String(cf.sqls), conversion: cf.mqls > 0 ? ((cf.sqls / cf.mqls * 100).toFixed(1) + '%') : '—' },
      { stage: 'Pipeline', value: cf.pipeline > 0 ? `$${(cf.pipeline / 1000).toFixed(0)}K` : '$0' },
    ];
  }, [channelFilter, rangeData]);

  const activeWoW = useMemo(() => {
    if (!channelFilter) return rangeData.weeklyComparison;
    return CHANNEL_ANALYTICS.wow[channelFilter] || rangeData.weeklyComparison;
  }, [channelFilter, rangeData]);

  const activeTrends = useMemo(() => {
    if (!channelFilter) return rangeData.trends;
    return CHANNEL_ANALYTICS.trends[channelFilter] || rangeData.trends;
  }, [channelFilter, rangeData]);

  const filteredChannels = useMemo(() => {
    if (!channelFilter) return ANALYTICS_DATA.channelROI;
    return ANALYTICS_DATA.channelROI.filter(c => c.id === channelFilter || c.id.startsWith(channelFilter));
  }, [channelFilter]);

  const sendMessage = (text, responseData) => {
    const userMsg = { role: 'user', text };
    const agentMsg = { role: 'agent', text: responseData?.text || ANALYTICS_CHAT_RESPONSES.general.text };
    setChatMessages(prev => [...prev, userMsg, agentMsg]);
    setChatMode(true);
  };

  const handleSend = () => { if (message.trim()) { sendMessage(message.trim(), null); setMessage(''); } };

  const handleInput = (e) => {
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 200) + 'px';
    setMessage(e.target.value);
  };

  /* ── Chat input ── */
  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={handleInput} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Dora about analytics..." rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="analytics-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.dora} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${tp}`}>Dora</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="analytics-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="analytics-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${hoverBg}`} data-testid="analytics-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Analytics</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => <ChatMessage key={i} msg={msg} isDark={isDark} />)}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  /* ── Deep Dive per channel (Priya-only) ── */
  if (deepDiveChannelId && isPriya) {
    const ddScenario = PRIYA_ANALYTICS_CHANNEL_DD[deepDiveChannelId];
    if (ddScenario) {
      return (
        <V4AdHocChat
          isDark={isDark}
          scenarioId={ddScenario.id}
          scenarios={{ [ddScenario.id]: ddScenario }}
          backLabel="Back to Analytics"
          onBack={() => setDeepDiveChannelId(null)}
        />
      );
    }
  }

  /* ── Keypress A (Priya-only) ── */
  if (analyticsAdHocScenario && isPriya) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={analyticsAdHocScenario}
        scenarios={PRIYA_ANALYTICS_KEYPRESS}
        backLabel="Back to Analytics"
        onBack={() => onAnalyticsAdHocDone?.()}
        skipFirstUserDelay
      />
    );
  }

  const activeChannelLabel = channelFilter ? CHANNEL_PILLS.find(p => p.id === channelFilter)?.label : null;

  return (
    <div className="flex flex-col h-full" data-testid="analytics-page">
      {/* Header row: title + date range + channel pills */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <BarChart3 className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Analytics</p>
            {activeChannelLabel && (
              <span className={`text-xs px-2 py-0.5 rounded-lg ${isDark ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>{activeChannelLabel}</span>
            )}
          </div>
          {/* Date Range Toggle + Actions */}
          <div className="flex items-center gap-1" data-testid="date-range-toggle">
            <Calendar className={`w-3.5 h-3.5 mr-1 ${ts}`} />
            {DATE_RANGES.map(r => (
              <button
                key={r.id}
                onClick={() => setDateRange(r.id)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${dateRange === r.id
                  ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20')
                  : `${border} ${ts} ${hoverBg}`}`}
                data-testid={`date-range-${r.id}`}
              >
                {r.label}
              </button>
            ))}
            <div className={`w-px h-4 mx-1 ${isDark ? 'bg-white/[0.08]' : 'bg-black/[0.08]'}`} />
            <button className={`w-7 h-7 rounded-lg flex items-center justify-center border transition-colors ${border} ${hoverBg}`} data-testid="analytics-sync-btn" title="Sync data">
              <RefreshCw className={`w-3.5 h-3.5 ${ts}`} />
            </button>
            <button className={`w-7 h-7 rounded-lg flex items-center justify-center border transition-colors ${border} ${hoverBg}`} data-testid="analytics-download-btn" title="Download report">
              <Download className={`w-3.5 h-3.5 ${ts}`} />
            </button>
          </div>
        </div>
        {/* Channel pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {CHANNEL_PILLS.map(pill => (
            <button
              key={pill.id || 'all'}
              onClick={() => setChannelFilter(pill.id)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-colors ${channelFilter === pill.id
                ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20')
                : `${border} ${ts} ${hoverBg}`}`}
              data-testid={`analytics-pill-${pill.id || 'all'}`}
            >
              {pill.label}
            </button>
          ))}
          {channelFilter && (
            <button onClick={() => setChannelFilter(null)} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] ${isDark ? 'text-white/40 hover:text-white/60' : 'text-slate-500 hover:text-slate-600'}`} data-testid="analytics-clear-filter">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* Main scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">

        {/* Row 1: Funnel + Data Snapshot */}
        <div className="flex items-start gap-5">
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-semibold mb-3 ${tp}`}>Marketing Funnel</p>
            <div className="overflow-x-auto">
              <HorizontalFunnel stages={activeFunnel} isDark={isDark} />
            </div>
          </div>
          <div className="w-[440px] flex-shrink-0">
            <WoWComparison data={activeWoW} isDark={isDark} rangeLabel={rangeData.label} />
          </div>
        </div>

        {/* Row 2: Trend cards */}
        <div>
          <p className={`text-xs font-semibold mb-3 ${tp}`}>Key Trends ({rangeData.label})</p>
          <div className="grid grid-cols-6 gap-3">
            {activeTrends.map((t, i) => <TrendCard key={i} item={t} isDark={isDark} />)}
          </div>
        </div>

        {/* Row 3: Channel table */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <p className={`text-xs font-semibold ${tp}`}>Channel Performance</p>
            <span className={`text-[10px] ${ts}`}>{filteredChannels.length} of {ANALYTICS_DATA.channelROI.length} channels</span>
          </div>
          <div className={`rounded-xl border ${border} overflow-hidden`}>
            <table className="w-full">
              <thead>
                <tr className={isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}>
                  <th className={`py-2.5 px-3 text-[10px] font-semibold text-left ${ts}`}>Channel</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}>Spend</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}>Leads</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}>CPL</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}>ROI</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}>Impressions</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}>Trend</th>
                  <th className={`py-2.5 px-2 text-[10px] font-semibold text-right ${ts}`}></th>
                </tr>
              </thead>
              <tbody>
                {filteredChannels.map(item => (
                  <ChannelRow key={item.id} item={item} isDark={isDark} isHighlighted={channelFilter && (item.id === channelFilter || item.id.startsWith(channelFilter))} onDeepDive={isPriya ? setDeepDiveChannelId : null} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Row 4: Dora's insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.dora} alt="" className="w-5 h-5 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Dora's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {ANALYTICS_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4MktAnalytics;
